<template>
    <div class="create-order-process-modal">
        <ProcessModal
            data-qa="create-order-modal"
            size="fullscreen"
            :is-open="isOpen"
            :title="$t('funnel.edit.createOrder.modal.title')"
            show-save
            :action-button-label="$t('funnel.edit.fulfillment.done')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #processParameters>
                <DsInlineAlert
                    v-if="createOrderItemInvalid && formSubmitted"
                    class="create-order-item-required"
                    data-qa="create-order-item-required"
                    leading-icon
                    type="critical"
                >
                    {{ $t('funnel.edit.createOrder.product.table.item.required') }}
                </DsInlineAlert>
                <div class="create-order-process-content">
                    <div v-if="isLoading" class="loading-data">
                        <DsProgressBar indeterminate />
                    </div>
                    <div v-else>
                        <label class="product-table-title">{{ tableItems }}</label>
                        <DsTableGrid class="create-order-table" :data="invoiceProductData">
                            <DsTableGridColumn :label="$t('funnel.edit.createOrder.product.table.item.header')" expand>
                                <template #default="{ index, name, description, longDescription, displayLongDesc }">
                                    <div
                                        class="product-table-item-wrapper"
                                        :data-qa="`${$options.TABLE_ID}${index}`"
                                    >
                                        <div class="product-table-item-product-name">
                                            {{ name }}
                                        </div>
                                        <div
                                            v-if="description && description.length > $options.MAX_DESCRIPTION_LENGTH"
                                            class="product-table-item-product-description"
                                        >
                                            <div v-if="!displayLongDesc">
                                                {{ `${description.substring(0, $options.MAX_DESCRIPTION_LENGTH)}...` }}
                                                <DsLink
                                                    class="more-icon"
                                                    @click="toggleDescriptionMode(index)"
                                                >
                                                    {{ $t('funnel.edit.createOrder.product.add.line.item.more') }}
                                                </DsLink>
                                            </div>
                                            <div v-else>
                                                {{ longDescription }}
                                                <DsLink
                                                    class="more-icon"
                                                    @click="toggleDescriptionMode(index)"
                                                >
                                                    {{ $t('funnel.edit.createOrder.product.add.line.item.less') }}
                                                </DsLink>
                                            </div>
                                        </div>
                                        <div v-else>
                                            {{ description }}
                                        </div>
                                    </div>
                                </template>
                            </DsTableGridColumn>
                            <DsTableGridColumn :label="$t('funnel.edit.createOrder.product.table.price.header')" prop="displayPrice" width="250" />
                            <DsTableGridColumn :label="$t('funnel.edit.createOrder.product.table.quantity.header')" width="150">
                                <template #default="product">
                                    <DsInputField
                                        v-model="product.quantity"
                                        type="number"
                                        name="number"
                                        :step="1"
                                        :min="1"
                                        @input="updateTotalPrice(product)"
                                    />
                                </template>
                            </DsTableGridColumn>
                            <DsTableGridColumn :label="$t('funnel.edit.createOrder.product.table.total.header')" width="250">
                                <template #default="product">
                                    {{ product.displayTotalPrice }}
                                    <DsIconButton
                                        name="trash-2"
                                        :data-qa="`${$options.DELETE_ITEM}${product.index}`"
                                        @click="removeProductLineItem(product)"
                                    />
                                </template>
                            </DsTableGridColumn>
                        </DsTableGrid>
                    </div>
                    <div class="add-line-item-wrapper">
                        <div v-if="!isAddProductEnabled">
                            <DsTextButton
                                data-qa="add-line-item"
                                @click="toggleAddProductMode"
                            >
                                <template #leading>
                                    <DsIcon name="add" />
                                </template>
                                <template #default>
                                    {{ $t('funnel.edit.createOrder.product.add.line.item.button') }}
                                </template>
                            </DsTextButton>
                        </div>
                        <div v-else>
                            <DsMultiselect
                                v-model="selectedProducts"
                                class="add-product-dropdown"
                                :add-text="addProductLabel"
                                :placeholder="$t('funnel.edit.createOrder.product.add.line.item.option.label')"
                                :options="productOptions"
                                bind-value-only
                                allow-add
                                searchable
                                multiple
                                help-text
                                @add="openCreateProduct"
                                @input="productSelected"
                                @updateSearch="handleSearchInputChange"
                            />
                        </div>
                    </div>
                    <hr class="divider" />
                    <div class="total-due-container">
                        <label>{{ $t('funnel.edit.createOrder.product.table.total.due') }}</label>
                        <label>{{ totalDue }}</label>
                    </div>
                    <div class="payment-mode-wrapper">
                        <div class="option-label">
                            {{ $t('funnel.edit.createOrder.payment.option.label') }}
                        </div>
                        <DsRadioGroup
                            v-model="selectedPaymentMode"
                            data-qa="selected-payment-mode"
                            bind-value-only
                            :options="paymentOptions"
                            @input="paymentTypeChanged"
                        />
                    </div>
                    <div v-if="showEmailInfo" id="email-info-container">
                        <div class="option-label">
                            {{ $t('funnel.edit.createOrder.createProduct.email.info') }}
                        </div>
                        <DsInputField
                            v-model="invoiceEmailFrom"
                            :label="$t('funnel.edit.createOrder.createProduct.email.from')"
                            class="email-info-input"
                            data-qa="email-info-from"
                            :content="invoiceEmailFrom"
                            :submitted="formSubmitted"
                            type="email"
                            required
                        >
                            <template #error>
                                {{ $t('funnel.edit.createOrder.createProduct.field.name.error') }}
                            </template>
                        </DsInputField>
                        <DsInputField
                            v-model="invoiceEmailSubject"
                            :label="$t('funnel.edit.createOrder.createProduct.email.subject')"
                            class="email-info-input"
                            data-qa="email-info-subject"
                            :content="invoiceEmailSubject"
                            :submitted="formSubmitted"
                            required
                        >
                            <template #error>
                                {{ $t('funnel.edit.createOrder.createProduct.field.name.error') }}
                            </template>
                        </DsInputField>
                        <div class="invoice-email-container">
                            <div class="invoice-email-preview">
                                <div
                                    v-if="invoiceEmailLogo !== ''"
                                    class="invoice-email-logo"
                                >
                                    <img :src="invoiceEmailLogo" />
                                </div>
                                <DsTextAreaField
                                    v-model="invoiceEmailMessage"
                                    class="email-message-area"
                                    data-qa="email-info-message"
                                    :content="invoiceEmailMessage"
                                />
                                <DsTextButton
                                    class="email-signature-preview"
                                >
                                    <template #default>
                                        {{ invoiceButtonLabel }}
                                    </template>
                                    <template #leading>
                                        <DsIcon name="file" />
                                    </template>
                                </DsTextButton>
                                <DsTextAreaField
                                    v-model="invoiceEmailSignature"
                                    class="email-signature-area"
                                    data-qa="email-info-signature"
                                    :content="invoiceEmailSignature"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </ProcessModal>
        <AddProductModal
            v-if="isCreateProductMode"
            :is-open="isCreateProductMode"
            :suggested-name="suggestedProductName"
            :is-spa-app="isSpaApp"
            @close-modal="closeCreateProduct"
            @create-product="createProduct"
        />
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';
import AddProductModal from '../addProductModal/AddProductModal.vue';
import {
    loadProductAndSubscriptionList,
    loadCreateOrderConfig,
    saveCreateOrderConfigAsReady,
    updateCreateOrderConfig,
    createOrderConfig,
    createProduct,
    createOrderItem,
    saveOrderItem,
    deleteOrderItem,
} from './create.order.api';

import { formatCurrency } from '../../../../utils/currency.util';

export const MAX_DESCRIPTION_LENGTH = 110;
export const TABLE_ID = 'item-table-identifier-';
export const DELETE_ITEM = 'item-table-delete-';
export const EMAIL_CONTAINER_ID = 'email-info-container';
export const ITEM_PREFIX = {
    PRODUCT: 'p:',
    SUBSCRIPTION: '-s:',
};
export const CREATE_ORDER_ITEM = {
    PRODUCT: 'products',
    SUBSCRIPTION: 'subscriptions',
};
export const PAYMENT_TYPE = {
    AUTO_CHARGE: 'AutoCharge',
    NO_CC_PAYABLE: 'AutoNoCCPayable',
    ALLOW_PAYMENT_YES: 'PayableInvoice',
    ALLOW_PAYMENT_NO: 'WithoutPayment',
};
const ADD_AS_TYPES = { PRODUCT: 'Product', SUBSCRIPTION: 'Subscription' };

export default {
    EMAIL_CONTAINER_ID,
    PAYMENT_TYPE,
    TABLE_ID,
    DELETE_ITEM,
    ITEM_PREFIX,
    ADD_AS_TYPES,
    CREATE_ORDER_ITEM,
    MAX_DESCRIPTION_LENGTH,
    components: {
        ProcessModal,
        AddProductModal,
    },


    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                },
            }),
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            createOrderConfigId: 0,
            createOrderConfiguration: {},
            invoiceEmailLogo: '',
            invoiceEmailFrom: '',
            invoiceEmailMessage: '',
            invoiceEmailSubject: '',
            invoiceEmailSignature: '',
            suggestedProductName: '',
            isCreateProductMode: false,
            isAddProductEnabled: false,
            products: [],
            productOptions: [],
            selectedProducts: [],
            invoiceProductData: [],
            isLoading: true,
            formSubmitted: false,
            selectedPaymentMode: '',
            paymentOptions: [],
        };
    },

    computed: {
        createOrderItemInvalid() {
            return this.invoiceProductData.length < 1;
        },

        invoiceButtonLabel() {
            return this.defaultEmailButton && this.defaultEmailButton !== ''
                ? this.defaultEmailButton
                : this.$t('funnel.edit.createOrder.createProduct.email.invoice');
        },

        showEmailInfo() {
            return this.selectedPaymentMode !== PAYMENT_TYPE.AUTO_CHARGE;
        },

        totalDue() {
            if (!this.invoiceProductData?.length || this.invoiceProductData.length === 0) {
                return formatCurrency(0);
            }
            const result = this.invoiceProductData.reduce((total, item) => total + (item.price * item.quantity), 0);

            return formatCurrency(result);
        },

        addProductLabel() {
            return this.suggestedProductName === ''
                ? this.$t('funnel.edit.createOrder.product.add.line.item.option.label')
                : `${this.$t('funnel.edit.createOrder.product.add.line.item.prefix')}${this.suggestedProductName}${this.$t('funnel.edit.createOrder.product.add.line.item.suffix')}`;
        },

        tableItems() {
            return this.invoiceProductData?.length > 0
                ? `${this.$t('funnel.edit.createOrder.product.table.item.plural')} (${this.invoiceProductData.length})`
                : this.$t('funnel.edit.createOrder.product.table.item.plural');
        },

        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
    },

    async mounted() {
        const paymentOptionsClassic = [
            { value: PAYMENT_TYPE.AUTO_CHARGE, label: this.$t('funnel.edit.createOrder.payment.options.autoCharge.nonPayableInvoice') },
            { value: PAYMENT_TYPE.NO_CC_PAYABLE, label: this.$t('funnel.edit.createOrder.payment.options.autoCharge.payableInvoice') },
            { value: PAYMENT_TYPE.ALLOW_PAYMENT_YES, label: this.$t('funnel.edit.createOrder.payment.options.manual.payableInvoice') },
            { value: PAYMENT_TYPE.ALLOW_PAYMENT_NO, label: this.$t('funnel.edit.createOrder.payment.options.manual.nonPayableInvoice') },
        ];

        const paymentOptionsSpaApp = [
            { value: PAYMENT_TYPE.ALLOW_PAYMENT_YES, label: this.$t('funnel.edit.createOrder.payment.options.manual.payableInvoice') },
            { value: PAYMENT_TYPE.ALLOW_PAYMENT_NO, label: this.$t('funnel.edit.createOrder.payment.options.manual.nonPayableInvoice') },
        ];

        this.paymentOptions = this.isSpaApp ? paymentOptionsSpaApp : paymentOptionsClassic;
        this.selectedPaymentMode = this.isSpaApp ? PAYMENT_TYPE.ALLOW_PAYMENT_YES : PAYMENT_TYPE.AUTO_CHARGE;
        await this.loadProductData();
        await this.loadConfig();
        this.processConfig();
        this.isLoading = false;
    },

    methods: {
        handleSearchInputChange(value) {
            this.suggestedProductName = value;
        },

        productSelected() {
            this.toggleAddProductMode();
            this.addSelectedOption();
        },

        addSelectedOption() {
            const newItems = this.findNewInvoiceItems(this.selectedProducts, this.invoiceProductData);

            if (newItems?.length > 0) {
                this.addInvoiceItem(newItems[0], this.findProductItem(newItems[0]));
            }
        },

        async createNewOrderItem(createOrderId, productId, productType) {
            const orderItem = await createOrderItem(createOrderId, productId, productType);

            return orderItem;
        },

        async addInvoiceItem(identifier, product) {
            if (product) {
                const productType = product.subscription ? CREATE_ORDER_ITEM.SUBSCRIPTION : CREATE_ORDER_ITEM.PRODUCT;
                const productId = product.subscription ? product.subscriptionPlanId : product.productId;
                const price = product.subscription ? product.planPrice : product.productPrice;
                const orderItem = await this.createNewOrderItem(this.createOrderConfigId, productId, productType);
                const invoiceItem = {
                    id: identifier,
                    orderItemId: orderItem.id,
                    productId,
                    productType,
                    parentId: orderItem.parentId,
                    index: this.invoiceProductData.length,
                    name: product.subscription ? product.subscriptionPlanName : product.productName,
                    quantity: 1,
                    description: product.productShortDesc || '-',
                    longDescription: product.productDesc || product.productShortDesc,
                    displayLongDesc: false,
                    price,
                    displayPrice: formatCurrency(price),
                    displayTotalPrice: formatCurrency(price),
                };

                this.invoiceProductData.push(invoiceItem);
            }
        },

        scrollToElement(elementId) {
            this.$nextTick(() => {
                const newRow = document.getElementById(elementId);

                if (newRow) {
                    newRow.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }
            });
        },

        findProductItem(identifier) {
            const elements = this.productOptions.filter((element) => element.value === identifier);

            if (elements?.length > 0) {
                return elements[0].data;
            }

            return undefined;
        },

        findNewInvoiceItems(identifiers, invoiceData) {
            const invoiceIds = new Set(invoiceData.map((item) => item.id));
            const elements = identifiers.filter((element) => !invoiceIds.has(element));

            return elements;
        },

        processConfig() {
            if (this.createOrderConfiguration && this.createOrderConfiguration.productList?.length > 0) {
                this.processSavedOrderItems(this.createOrderConfiguration.productList);
            }
        },

        processSavedOrderItems(savedOrderItems) {
            this.invoiceProductData = this.loadSavedInvoiceProductData(savedOrderItems);
            this.processRepeatedOrderItems();
            this.selectedProducts = this.invoiceProductData.map((item) => {
                return item.id;
            });
        },

        processRepeatedOrderItems() {
            const invoiceIds = new Set();

            for (const item of this.invoiceProductData) {
                if (invoiceIds.has(item.id)) {
                    item.id = `${item.id}(${item.index})`;
                }
                invoiceIds.add(item.id);
            }
        },

        createInvoiceItemIdentifier(isSubscription, productId, parentId) {
            const identifier = isSubscription
                ? `${ITEM_PREFIX.PRODUCT}${productId}${ITEM_PREFIX.SUBSCRIPTION}${parentId}`
                : `${ITEM_PREFIX.PRODUCT}${productId}`;

            return identifier;
        },

        loadSavedInvoiceProductData(savedOrderItems) {
            return savedOrderItems.map((item, index) => {
                const {
                    id,
                    productId,
                    parentId,
                    unitPrice,
                    quantity,
                    parentName,
                    parentDescription,
                    parentType,
                } = item;
                const isSubscription = parentType === ADD_AS_TYPES.SUBSCRIPTION;
                const productType = isSubscription ? CREATE_ORDER_ITEM.SUBSCRIPTION : CREATE_ORDER_ITEM.PRODUCT;
                const identifier = this.createInvoiceItemIdentifier(isSubscription, productId, parentId);
                const displayPrice = formatCurrency(unitPrice);
                const displayTotalPrice = formatCurrency(unitPrice * quantity);

                return {
                    id: identifier,
                    orderItemId: id,
                    index,
                    productId,
                    productType,
                    parentId,
                    name: parentName,
                    description: parentDescription,
                    longDescription: parentDescription,
                    displayLongDesc: false,
                    price: unitPrice,
                    quantity,
                    displayPrice,
                    displayTotalPrice,
                };
            });
        },
        async removeProductLineItem(product) {
            await deleteOrderItem(this.invoiceProductData[product.index].orderItemId);

            this.selectedProducts = this.selectedProducts.filter((item) => item !== this.invoiceProductData[product.index].id);
            this.invoiceProductData = this.invoiceProductData.filter((item) => item.id !== this.invoiceProductData[product.index].id);
        },
        updateTotalPrice(product) {
            if (product.index >= 0 && product.index < this.invoiceProductData.length) {
                const currentRow = this.invoiceProductData[product.index];

                currentRow.displayTotalPrice = formatCurrency(product.price * product.quantity);
            }
        },
        toggleAddProductMode() {
            this.isAddProductEnabled = !this.isAddProductEnabled;
        },
        toggleDescriptionMode(position) {
            this.invoiceProductData[position].displayLongDesc = !this.invoiceProductData[position].displayLongDesc;
        },
        async createProduct(newProduct) {
            const isSubscription = newProduct.addAs === ADD_AS_TYPES.SUBSCRIPTION;
            const productType = isSubscription ? CREATE_ORDER_ITEM.SUBSCRIPTION : CREATE_ORDER_ITEM.PRODUCT;
            const product = await createProduct(newProduct);
            const itemId = isSubscription ? product.subscriptionId : product.id;
            const orderItem = await this.createNewOrderItem(this.createOrderConfigId, itemId, productType);
            const identifier = this.createInvoiceItemIdentifier(isSubscription, product.id, product.subscriptionId);
            const productOptions = this.createOptions(isSubscription, newProduct, product, orderItem);
            const invoiceItem = {
                id: identifier,
                orderItemId: orderItem.id,
                productId: itemId,
                productType,
                parentId: orderItem.parentId,
                index: this.invoiceProductData.length,
                name: orderItem.parentName,
                quantity: orderItem.quantity,
                description: orderItem.parentDescription || '-',
                longDescription: product.desc || product.shortDesc,
                displayLongDesc: false,
                price: orderItem.unitPrice,
                displayPrice: formatCurrency(orderItem.unitPrice),
                displayTotalPrice: formatCurrency(orderItem.unitPrice * orderItem.quantity),
            };

            this.addNewProductToCollections(productOptions, invoiceItem);
            this.closeCreateProduct();
            this.toggleAddProductMode();
        },

        createOptions(isSubscription, productReference, product, orderItem) {
            const response = [];
            const productItem = {
                productId: product.id,
                subscription: isSubscription,
                productName: product.name,
                productPrice: productReference.price,
                productShortDesc: product.shortDesc,
                productDesc: product.desc,
                subscriptionPlanId: 0,
                subscriptionPlanName: '',
                planPrice: 0,
            };
            const subscriptionItem = { ...productItem };

            if (isSubscription) {
                subscriptionItem.subscriptionPlanId = product.subscriptionId;
                subscriptionItem.subscriptionPlanName = orderItem.parentName;
                subscriptionItem.planPrice = productReference.planPrice;

                response.push({
                    value: `${ITEM_PREFIX.PRODUCT}${product.id}${ITEM_PREFIX.SUBSCRIPTION}${product.subscriptionId}`,
                    label: subscriptionItem.subscriptionPlanName,
                    data: subscriptionItem,
                });
            }

            if (!product.subscriptionOnly) {
                response.push({
                    value: `${ITEM_PREFIX.PRODUCT}${product.id}`,
                    label: productItem.productName,
                    data: productItem,
                });
            }

            return response;
        },

        addNewProductToCollections(productOptions, invoiceItem) {
            if (productOptions.length > 0) {
                this.productOptions.push(...productOptions);
                this.selectedProducts.push(invoiceItem.identifier);
                this.invoiceProductData.push(invoiceItem);
            }
        },

        openCreateProduct(value) {
            this.suggestedProductName = value;
            this.isCreateProductMode = true;
        },

        closeCreateProduct() {
            this.suggestedProductName = '';
            this.isCreateProductMode = false;
        },

        async loadProductData() {
            const products = await loadProductAndSubscriptionList();

            this.products = [...products];
            this.productOptions = this.processItemsToOptions(products);
        },

        processItemsToOptions(data) {
            return data.map((option) => {
                const item = {};
                let subscriptionItem = false;

                item.value = `${ITEM_PREFIX.PRODUCT}${option.productId}`;
                item.label = option.productName;
                item.data = option;

                if (option.subscription && option.subscriptionPlanId > 0) {
                    item.value = `${item.value}${ITEM_PREFIX.SUBSCRIPTION}${option.subscriptionPlanId}`;
                    item.label = option.subscriptionPlanName;
                    subscriptionItem = true;
                }

                item.data.subscription = subscriptionItem;

                return item;
            });
        },

        async loadConfig() {
            const { value } = this.cell;

            if (!value) {
                return;
            }

            if (!value.createOrderConfigId) {
                this.createOrderConfiguration = await this.createInitialOrderConfig();
                this.createOrderConfigId = this.createOrderConfiguration.id;
            }

            this.createOrderConfigId = value.createOrderConfigId || this.createOrderConfigId;
            this.createOrderConfiguration = await loadCreateOrderConfig(this.createOrderConfigId);

            if (this.createOrderConfiguration) {
                this.selectedPaymentMode = this.createOrderConfiguration.paymentType;
                this.invoiceEmailLogo = this.createOrderConfiguration.invoiceEmailLogo;
                this.defaultEmailButton = this.createOrderConfiguration.defaultEmailButton;
                this.invoiceEmailFrom = this.createOrderConfiguration.invoiceEmailFrom;
                this.invoiceEmailSubject = this.createOrderConfiguration.invoiceEmailSubject;
                this.invoiceEmailMessage = this.createOrderConfiguration.invoiceEmailMessage;
                this.invoiceEmailSignature = this.createOrderConfiguration.invoiceEmailSignature;
            }
        },

        async createInitialOrderConfig() {
            this.createOrderConfiguration = await createOrderConfig();

            if (this.createOrderConfiguration?.id) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};

                cell.value.createOrderConfigId = this.createOrderConfiguration.id;

                const eventData = {
                    cell: this.cell,
                    preventClose: true,
                    isReady: false,
                };

                this.$emit('update-cell-value', eventData);

                return this.createOrderConfiguration;
            }

            return { id: 0 };
        },

        closeModal() {
            if (this.createOrderItemInvalid) {
                const eventData = {
                    cell: this.cell,
                    preventClose: false,
                    isReady: false,
                };

                this.$emit('update-cell-value', eventData);
            } else {
                this.$emit('close-modal');
            }
        },

        paymentTypeChanged() {
            if (this.selectedPaymentMode !== PAYMENT_TYPE.AUTO_CHARGE) {
                this.scrollToElement(EMAIL_CONTAINER_ID);
            }
        },

        async updateConfig() {
            this.formSubmitted = true;

            if (this.isReady()) {
                const hasEmailInfo = this.selectedPaymentMode !== PAYMENT_TYPE.AUTO_CHARGE;
                const cell = { ...this.cell };

                cell.value = cell.value || {};

                const eventData = {
                    cell: this.cell,
                    preventClose: false,
                };
                const payload = {
                    paymentType: this.selectedPaymentMode,
                    invoiceEmailFrom: hasEmailInfo ? this.invoiceEmailFrom : null,
                    invoiceEmailSubject: hasEmailInfo ? this.invoiceEmailSubject : null,
                    invoiceEmailMessage: hasEmailInfo ? this.invoiceEmailMessage : null,
                    invoiceEmailSignature: hasEmailInfo ? this.invoiceEmailSignature : null,
                };

                await this.saveAllOrderItems(cell.value.createOrderConfigId);
                await updateCreateOrderConfig(cell.value.createOrderConfigId, payload);
                await saveCreateOrderConfigAsReady(cell.value.createOrderConfigId, payload);

                this.formSubmitted = false;

                this.$emit('update-cell-value', eventData);
            }
        },

        async saveAllOrderItems(createOrderConfigId) {
            const promises = this.invoiceProductData.map(async (item) => {
                try {
                    const orderItem = {
                        quantity: item.quantity,
                        parentId: item.parentId,
                    };

                    await saveOrderItem(createOrderConfigId, item.orderItemId, item.productType, orderItem);
                } catch (error) {
                    this.$error({ message: `Error saving createOrderItem:${item.orderItemId}` });
                }
            });

            await Promise.all(promises);
        },

        isReady() {
            let response = true;

            if (this.createOrderItemInvalid) {
                response = false;
            } else if (this.selectedPaymentMode !== PAYMENT_TYPE.AUTO_CHARGE
                && (this.invoiceEmailFrom === '' || this.invoiceEmailSubject === '')) {
                response = false;
                this.scrollToElement(EMAIL_CONTAINER_ID);
            }

            return response;
        },
    },
};
</script>

<style lang="scss" scoped>
.product-table-title {
    padding-bottom: $spacing-200;
    font-size: px-to-rem(18);
    font-weight: 400;
    line-height: $font-size-xl;
    letter-spacing: 0;

}
.add-line-item-wrapper {
    padding: $spacing-150 $spacing-200 $spacing-150;
}
.dropdown-menu-item.add-item {
    color: $color-blue-700;
    grid-template-columns: 2rem 1fr;
}

.save-status-container {
    position: absolute;
    top: px-to-rem(18);
    right: px-to-rem(96);
}
.dropdown-menu-list {
    padding: 0;
}
.dropdown-menu-item {
    display: grid;
    grid-template-columns: 1fr;
}
.product-table-item-wrapper {
    display: grid;
    grid-gap: $spacing-200;
}

.product-table-item-product-description {
    width: px-to-rem(400);

    .more-icon,
    .more-icon:hover {
        color: $color-blue-700;
    }
}
.add-product-dropdown {
    margin: auto 0 auto;
    width: px-to-rem(300);
}
.product-table-item-product-name {
    font-size: $font-size-md;
    line-height: $line-height-lg;
    font-weight: 400;
    letter-spacing: 0;

}
.product-table-item-product-description {
    font-size: $font-size-sm;
    line-height: $font-size-lg;
    font-weight: 400;
    letter-spacing: 0;
    color: $color-gray-800;

}
.product-menu-line-item-price {
    white-space: nowrap;
}
hr.divider {
    border-color: $color-gray-200;
    margin: 0;
}
.option-label {
    font-size: $font-size-xs;
    line-height: $line-height-sm;
    color: $color-ink-800;
}
.create-order-process-content {
    border: px-to-rem(1) solid $color-gray-400;
    padding: $spacing-400 $spacing-500;
    margin-top: $spacing-400;
    border-radius: px-to-rem(8);
    min-height: 88vh;
}
.invoice-email-container {
    border: px-to-rem(1) solid $color-gray-400;
    padding: $spacing-400 $spacing-500;
    border-radius: px-to-rem(8);
    text-align: center;
}
.invoice-email-preview {
    display: inline-block;
    width: 60%;
    margin: 0 auto;
}
.invoice-email-logo {
    max-height: px-to-rem(144);
    max-width: px-to-rem(144);
}
.loading-data {
    margin-top: $spacing-400;
    height: 20vh;
}

.create-order-table {
    min-height: 15vh;
}
.total-due-container{
    float: right;
    margin-top: $spacing-400;
    padding-right: $spacing-800;

    label {
        padding-right: $spacing-800;
    }
}
.payment-mode-wrapper {
    padding-top: 8rem;
}

#email-info-container {
    padding-top: $spacing-800;
}

.email-info-input {
    width: 60%;
}

#email-info-container > .option-label {
    padding-bottom: $spacing-200;
}

#email-info-container .email-signature-preview {
    margin: auto;
}

#email-info-container .email-message-area {
    margin-bottom: 0;
}

.create-order-item-required {
    margin-top: $spacing-300;
}

</style>
<style lang="scss">
div.create-order-process-modal div.table-grid.create-order-table table > tbody > tr > td {
    vertical-align: baseline;
}
div.create-order-process-modal div.table-grid.create-order-table table > thead  {
    border: none;
    font-size: $font-size-xs;
    font-weight: 500;
    line-height: $font-size-md;
    letter-spacing: 0;

}
div.create-order-process-modal div.dropdown-reference div.multiselect-tags div.chip.with-interaction {
    display: none;
}

div.create-order-process-modal .create-order-table thead th:last-child div.cell {
    padding-right: $spacing-600;
    float: right;
}


div.create-order-process-modal .create-order-table tbody td:last-child {
    text-align: right;
}

div.create-order-process-modal #email-info-container .invoice-email-container .email-message-area textarea {
    height: 6rem;
    border-style: dashed;
}

div.create-order-process-modal #email-info-container .invoice-email-container .email-signature-area textarea {
    height: 10rem;
    border-style: dashed;
}
</style>
